import React, {useEffect, useState} from 'react';
import ReactGA from "react-ga4";
import {useParams} from 'react-router-dom';
import '../../style/Hostage.css';
import {
  Box,
  Button,
  CardMedia,
  Container, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Activity, Hostage, HostageTextData, User} from '../types';
import { decode } from '../../utils/Utils';
import ImageCarousel from './ImageCarousel';
import { HostageTabs } from "./HostageTabs";
import JoinedHostageDrawer from "./JoinedHostageDrawer";

const HostagePage = () => {
  const {t, i18n} = useTranslation();
  const {id} = useParams();
  const [hostage, setHostage] = useState<Hostage | null>(null);
  const [hostageTextData, setHostageTextData] = useState<HostageTextData | null>(null);
  const [events, setEvents] = useState<Activity[]>([]);
  const [isJoinedDrawerOpen, setIsJoinedDrawerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [registeredUsersMap, setRegisteredUsersMap] = useState<Record<string, number>>({});
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]); // Store URLs for display
  const [profilePhotoIndex, setProfilePhotoIndex] = useState<number>(0); // Index of the profile photo
  const [heText, setHeText] = useState<string | undefined>('');
  const [enText, setEnText] = useState<string | undefined>('');
  const [photos, setPhotos] = useState<File[]>([]);

  useEffect(() => {
    // Fetch data based on the item id
    fetchHostageData();
    getHostagesEvents();
    initNumOfRegisteredUsers(events);
  }, [id]);

  useEffect(() => {
    if (hostage) {
      const initialPhotoUrls = [];
      initialPhotoUrls.push(hostage.profilePicture);
      if (hostage.pictures) {
        initialPhotoUrls.push(...hostage.pictures.filter(photo => photo.includes('images')));
      }
      setPhotoUrls(initialPhotoUrls);
    }
  }, [hostage]);

  useEffect(() => {
    if (!hostage) {
      return;
    }
    setHostageTextData(hostage[i18n.language as  "en" | "he"]);
  }, [i18n.language]);

  if (!hostage || !hostageTextData) {
    return <div>{t('hostageCard.loading')}</div>; // Show a loading message while fetching data
  }

  if (!user) {
    return null;
  }

  function fetchHostageData() {
    fetch(`/api/hostage/${id}`, {
      credentials: 'include' // include cookies in the request
    })
    .then(response => response.json())
    .then(res => {
      setHostage(res.data);
      setUser(res.user);
      setHeText(res.data.he.text);
      setEnText(res.data.en.text);
      setHostageTextData(res.data[i18n.language as "en" | "he"]);
    })
    .catch(err => {
      console.error('Error fetching hostage data:', err);
      console.log(`No data found for id: ${id}, redirecting to home`);
      navigate("/"); // Redirect to home if no data is found
    });
  }

  const closeDrawer = () => {
    setIsJoinedDrawerOpen(false);
    window.location.reload();
  }


  function getHostagesEvents() {
    fetch(`/api/activity/byHostage/${id}`, {
      credentials: 'include' // include cookies in the request
    })
    .then(response => response.json())
    .then(res => {
      setEvents(res);
      initNumOfRegisteredUsers(res);
    })
    .catch(err => {
      console.error('Error fetching hostage data:', err);
    });
  }

  function initNumOfRegisteredUsers(events: Activity[]) {
    const userCountMap: Record<string, number> = {};

    events.forEach((activity) => {
      if (activity.id) {
        userCountMap[activity.id] = activity.registeredUsersCount;
      }
    });

    setRegisteredUsersMap(userCountMap);
  }



  const handleSupportClick = async () => {
    try {
      const response = await fetch('/api/user/support', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({hostageId: id})
      });
      const res = await response.json();
      console.log('Support request successful:', res);
      setIsJoinedDrawerOpen(true);

      ReactGA.event({
        category: "Event",
        action: "Join Hostage Clicked",
        label: id
      });
    } catch (err) {
      console.error('Error sending support request:', err);
    }
  };

  const handleBackToList = () => {
    window.location.href = '/home';
  };

  const handleToggleReadMore = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };


  // edit images
  async function getBase64Photo(uploadedPhoto: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        resolve(event.target?.result?.toString() || '');
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(uploadedPhoto!);
    });
  }

  async function updateHostageData() {
    if (!hostage) {
      return;
    }
    // Separate new photos (Files) from old photos (URLs)
    const newPhotos = photos.map((file) => getBase64Photo(file));
    const oldPhotoUrls = photoUrls.filter((url, index) => !photos.some((file, i) => index === photoUrls.length - photos.length + i));

    // Get base64 for new photos
    const base64Photos = await Promise.all(newPhotos);

    // Combine new photos and old photos
    const allPhotos = [...oldPhotoUrls, ...base64Photos];

    // Determine the profile photo
    const profilePicture = allPhotos[profilePhotoIndex];
    const photo = profilePicture;
    const pictures = allPhotos.filter((_, index) => index !== profilePhotoIndex);
    const he = hostage.he;
    const en = hostage.en;
    if (heText) {
      he.text = heText;
    }
    if (enText) {
      en.text = enText;
    }
    const body = {
      he,
      en,
      photo,
      profilePicture,
      pictures
    };

    try {
      if (!hostage?.id) {
        console.log('Error - no hostage id');
        return;
      }
      const response = await fetch(`/admin/hostage`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HOSTAGE-ID': hostage.id
        },
        body: JSON.stringify(body)
      });

      const res = await response.json();
      setHostage(res.data);
    } catch (err) {
      console.error('Error updating hostage data:', err);
    }
  }
  const handleEditHostage = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    updateHostageData();
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHeText(hostage.he.text);
    setEnText(hostage.en.text);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newPhotos = Array.from(e.target.files);
        setPhotos((prevPhotos) => [...prevPhotos, ...newPhotos]);

      const newPhotoUrls = Array.from(e.target.files).map((file) => URL.createObjectURL(file));
      setPhotoUrls((prevUrls) => [...prevUrls, ...newPhotoUrls]);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));

    if (profilePhotoIndex === index) {
      setProfilePhotoIndex(0); // If the profile photo is removed, set the first one as default
    } else if (profilePhotoIndex > index) {
      setProfilePhotoIndex((prevIndex) => prevIndex - 1); // Adjust the index if the removed photo was before the profile photo
    }
  };

  const isJoined = user.supported_hostages.includes(hostage.id);

  const hostagePhotos = [hostage.profilePicture, ...(hostage.pictures?.filter(photo => photo.includes('images')) ?? [])];

  function canEdit(hostage: Hostage) {
    return user?.role === 'admin' ||
      (user?.role === 'moderator' && user?.moderating?.includes(hostage.id));
  }

  const isBibas = hostage.id == '66db418882f0f326e6972b32';

  return (
    <Container maxWidth="sm" className="hostage-card-container">
      <div className="sticky-header">
        <Button
          onClick={handleBackToList}
          className='back-to-list-btn'
        >
          {t('hostageCard.backToList')}
        </Button>
      </div>

      <Box className="hostage-card-box">
        <Box 
          sx={{
            backgroundColor: '#7A7A7A', 
            color: 'white', 
            textAlign: 'center', 
            padding: '8px',
          }}
        >
      {isBibas && (hostage.isBack ? t('story.waitedForYouMany') : t('story.waitingForYouMany' ))}
      {
  !isBibas && (hostage.isBack
    ? decode(t('story.waitedForYou', { name:  hostage[i18n.language as "en" | "he"].first_name }))
    : decode(t('story.waitingForYou', { name:  hostage[i18n.language as "en" | "he"].first_name }))
  )
}
        </Box>
        <ImageCarousel photos={hostagePhotos} altText={hostageTextData.name_with_title} />

        <CardMedia
          className="image-box"
          component="img"
          image={hostage.photo}
          alt={hostageTextData.name_with_title}
        />
        <Box className="hostage-overlay">
          <Box className="hostage-text-container">
            <Typography variant="h6" className="hostage-name-title">
              {hostageTextData.name_with_title}, {hostage.age}
            </Typography>
            <Typography
              variant="body2"
              className={`hostage-text ${isDescriptionExpanded ? "expanded" : ""}`}
            >
              {hostageTextData.text}
            </Typography>
            <Button onClick={handleToggleReadMore} className='hostage-text-read-more'>
              {isDescriptionExpanded ? t('story.readLess') + "..." : t('story.readMore') + "..."}
            </Button>
          </Box>
        </Box>
      </Box>
      <div>
        {canEdit(hostage) && !isEditing ? (
          <Button onClick={handleEditHostage}>Edit</Button>
        ) : null}

      </div>


      <Dialog open={isEditing} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>{t('hostageCard.editImage')}</DialogTitle>
        <DialogContent>
          <h3>תיאור בעברית</h3>
          <TextField
            fullWidth
            multiline
            rows={5}
            value={heText}
            onChange={(e) => setHeText(e.target.value)}
            // label={t('hostageCard.editText')}
            variant="outlined"
          />
          <h3>English description</h3>
          <TextField
            fullWidth
            multiline
            rows={5}
            value={enText}
            onChange={(e) => setEnText(e.target.value)}
            // label={t('hostageCard.editText')}
            variant="outlined"
          />
          <div style={{ marginTop: '20px' }}>
            <label>{t('createActivity.addImage')}</label>
            <input type="file" accept="image/*" multiple onChange={handleFileChange} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
              {photoUrls.map((url, index) => (
                <div key={index} style={{ border: profilePhotoIndex === index ? '2px solid green' : '1px solid gray', padding: '10px', width: '120px', maxWidth: "90%" }}>
                  <img
                    src={url}
                    alt={`Photo ${index + 1}`}
                    style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                  />
                  {/* Controls container positioned below the image */}
                  <div style={{ marginTop: '10px', textAlign: 'center' }}>
                    <label style={{ display: 'block', fontSize: '12px' }}>
                      <input
                        type="radio"
                        name="profilePhoto"
                        value={index}
                        checked={profilePhotoIndex === index}
                        onChange={() => setProfilePhotoIndex(index)}
                        style={{ marginRight: '5px' }}
                      />
                      {t('createActivity.isProfilePhoto')}
                    </label>
                    <Button variant="text" color="secondary" onClick={() => handleRemovePhoto(index)} style={{ fontSize: '12px' }}>
                      {t('createActivity.removePhoto')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ margin: '10px' }} >
          <Button style={{ margin: '5px' }} onClick={handleSave} variant="contained" color="primary">
            {t('hostageCard.save')}
          </Button>
          <Button style={{ margin: '5px' }} onClick={handleCancel} variant="outlined">
            {t('hostageCard.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {!isJoined &&
        <Button
          variant="contained"
          className="hostage-join-btn"
          onClick={handleSupportClick}>
          {decode(t('hostageCard.supportBtn', { name: hostageTextData.first_name }))}
        </Button>
      }
      <HostageTabs />
      <JoinedHostageDrawer
        open={isJoinedDrawerOpen}
        onClose={closeDrawer}
        hostage={hostage}
      />
    </Container>
  );
};


export default HostagePage;

